import {
    getWebPushConfig,
    subscribeTenantWebPush,
    unsubscribeTenantWebPush,
    WebPushSubscriptionPayload
} from "./Api";
import {
    getAccountWebPushConfig,
    subscribeAccountWebPush,
    unsubscribeAccountWebPush
} from "./AccountApi";
import { getCurrentTheme } from "theme";

const SERVICE_WORKER_PATH = "/office-push-sw.js";
const TENANT_SUBSCRIPTIONS_KEY = "galactic-webpush-tenants";
const ACCOUNT_SUBSCRIPTIONS_KEY = "galactic-webpush-accounts";
const WEB_PUSH_CLIENT_ID_KEY = "galactic-webpush-client-id";

function getServiceWorkerPath(): string {
    const theme = getCurrentTheme();
    const query = new URLSearchParams({
        title: theme.brand.companyName,
        icon: theme.assets.pwaIcon512,
        badge: theme.assets.pwaIcon192,
    });
    return `${SERVICE_WORKER_PATH}?${query.toString()}`;
}

let fallbackWebPushClientId: string | undefined;
let hasSyncedEnabledTenantWebPushSubscriptions = false;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = "=".repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, "+")
        .replace(/_/g, "/");

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

function loadSubscriptions(key: string): string[] {
    try {
        return JSON.parse(localStorage.getItem(key) || "[]");
    } catch (error) {
        return [];
    }
}

function saveSubscriptions(key: string, ids: string[]) {
    localStorage.setItem(key, JSON.stringify(Array.from(new Set(ids))));
}

function markTenantSubscribed(tenantId: string) {
    saveSubscriptions(TENANT_SUBSCRIPTIONS_KEY, [...loadSubscriptions(TENANT_SUBSCRIPTIONS_KEY), tenantId]);
}

function markTenantUnsubscribed(tenantId: string) {
    saveSubscriptions(
        TENANT_SUBSCRIPTIONS_KEY,
        loadSubscriptions(TENANT_SUBSCRIPTIONS_KEY).filter(id => id !== tenantId)
    );
}

function markAccountSubscribed(accountId: string) {
    saveSubscriptions(ACCOUNT_SUBSCRIPTIONS_KEY, [...loadSubscriptions(ACCOUNT_SUBSCRIPTIONS_KEY), accountId]);
}

function markAccountUnsubscribed(accountId: string) {
    saveSubscriptions(
        ACCOUNT_SUBSCRIPTIONS_KEY,
        loadSubscriptions(ACCOUNT_SUBSCRIPTIONS_KEY).filter(id => id !== accountId)
    );
}

function createWebPushClientId(): string {
    if (window.crypto && typeof window.crypto.randomUUID === "function") {
        return window.crypto.randomUUID();
    }
    return `webpush-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function getWebPushClientId(): string {
    try {
        const existingClientId = localStorage.getItem(WEB_PUSH_CLIENT_ID_KEY);
        if (existingClientId) {
            return existingClientId;
        }

        const clientId = createWebPushClientId();
        localStorage.setItem(WEB_PUSH_CLIENT_ID_KEY, clientId);
        return clientId;
    } catch (error) {
        fallbackWebPushClientId = fallbackWebPushClientId || createWebPushClientId();
        return fallbackWebPushClientId;
    }
}

function subscriptionToPayload(subscription: PushSubscription): WebPushSubscriptionPayload {
    const payload = subscription.toJSON() as Omit<WebPushSubscriptionPayload, "clientId">;
    return {
        ...payload,
        clientId: getWebPushClientId()
    };
}

export function webPushSupported(): boolean {
    return "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function tenantPushEnabledLocally(tenantId: string): boolean {
    return loadSubscriptions(TENANT_SUBSCRIPTIONS_KEY).includes(tenantId);
}

export function accountPushEnabledLocally(accountId: string): boolean {
    return loadSubscriptions(ACCOUNT_SUBSCRIPTIONS_KEY).includes(accountId);
}

async function getOrCreatePushSubscription(
    getConfig: () => Promise<{ public_key: string }>
): Promise<PushSubscription> {
    const config = await getConfig();
    if (!config.public_key) {
        throw new Error("VAPID public key ontbreekt");
    }
    const registration = await navigator.serviceWorker.register(getServiceWorkerPath());
    const existingSubscription = await registration.pushManager.getSubscription();
    return existingSubscription || registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.public_key)
    });
}

export async function enableTenantWebPush(tenantId: string): Promise<void> {
    if (!webPushSupported()) {
        throw new Error("Webpush wordt niet ondersteund op dit device");
    }

    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error("Push meldingen zijn niet toegestaan");
    }

    const subscription = await getOrCreatePushSubscription(getWebPushConfig);
    await subscribeTenantWebPush(tenantId, subscriptionToPayload(subscription));
    markTenantSubscribed(tenantId);
}

export async function syncEnabledTenantWebPushSubscriptions(): Promise<void> {
    if (hasSyncedEnabledTenantWebPushSubscriptions) {
        return;
    }
    hasSyncedEnabledTenantWebPushSubscriptions = true;

    const tenantIds = loadSubscriptions(TENANT_SUBSCRIPTIONS_KEY);
    if (tenantIds.length === 0 || !webPushSupported() || Notification.permission !== "granted") {
        return;
    }

    const subscription = await getOrCreatePushSubscription(getWebPushConfig);
    const payload = subscriptionToPayload(subscription);

    await Promise.all(tenantIds.map(tenantId => subscribeTenantWebPush(tenantId, payload)));
}

export async function disableTenantWebPush(tenantId: string): Promise<void> {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
    const subscription = await registration?.pushManager.getSubscription();

    await unsubscribeTenantWebPush(tenantId, subscription?.endpoint || "", getWebPushClientId());
    markTenantUnsubscribed(tenantId);
}

export async function enableAccountWebPush(accountId: string): Promise<void> {
    if (!webPushSupported()) {
        throw new Error("Webpush wordt niet ondersteund op dit device");
    }
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error("Push meldingen zijn niet toegestaan");
    }
    const subscription = await getOrCreatePushSubscription(getAccountWebPushConfig);
    await subscribeAccountWebPush(accountId, subscriptionToPayload(subscription));
    markAccountSubscribed(accountId);
}

export async function syncEnabledAccountWebPushSubscriptions(): Promise<void> {
    const accountIds = loadSubscriptions(ACCOUNT_SUBSCRIPTIONS_KEY);
    if (accountIds.length === 0 || !webPushSupported() || Notification.permission !== "granted") {
        return;
    }
    const subscription = await getOrCreatePushSubscription(getAccountWebPushConfig);
    const payload = subscriptionToPayload(subscription);
    await Promise.all(accountIds.map(accountId => subscribeAccountWebPush(accountId, payload)));
}

export async function disableAccountWebPush(accountId: string): Promise<void> {
    const registration = await navigator.serviceWorker.getRegistration(SERVICE_WORKER_PATH);
    const subscription = await registration?.pushManager.getSubscription();
    await unsubscribeAccountWebPush(accountId, subscription?.endpoint || "", getWebPushClientId());
    markAccountUnsubscribed(accountId);
}
